import { z } from 'zod';
import { landingContentSchema, seoSchema } from './landing.ts';

export const pageKinds = ['SERVICE', 'SOLUTION', 'INDUSTRY', 'CORE', 'CAMPAIGN', 'SEO'] as const;
export const pageKindSchema = z.enum(pageKinds);
export type PageKindValue = z.infer<typeof pageKindSchema>;
export const landingStates = ['DRAFT', 'REVIEW', 'SCHEDULED', 'ARCHIVED'] as const;
export const translationStatuses = ['NOT_STARTED', 'DRAFT', 'NEEDS_REVIEW', 'APPROVED', 'PUBLISHED'] as const;
export const templateKeys = ['enterprise-service', 'startup-mvp', 'crm', 'mobile-app', 'ecommerce', 'industry', 'lead-gen-minimal', 'long-form-seo', 'comparison'] as const;

/** What a page is for (step 1 §15 step 1): decides URL family, indexing default and whether ads are allowed. */
export const kindInfo: Record<PageKindValue, { label: string; prefix: string; indexDefault: boolean }> = {
  SERVICE: { label: 'Service', prefix: 'services/', indexDefault: true },
  SOLUTION: { label: 'Solution', prefix: 'solutions/', indexDefault: true },
  INDUSTRY: { label: 'Industry', prefix: 'industries/', indexDefault: true },
  CORE: { label: 'Core page', prefix: '', indexDefault: true },
  CAMPAIGN: { label: 'Campaign-only landing', prefix: 'lp/', indexDefault: false },
  SEO: { label: 'SEO landing', prefix: '', indexDefault: true },
};

export const landingCreateSchema = z.object({
  name: z.string().trim().min(2).max(120),
  kind: pageKindSchema,
  locale: z.string().trim().min(2).max(10),
  /** Path without locale. Omitted: derived from the name by the slug engine. */
  slug: z.string().trim().max(200).optional(),
  serviceKey: z.string().trim().max(60).optional(),
  marketCode: z.string().trim().max(10).optional(),
  template: z.enum(templateKeys).default('lead-gen-minimal'),
  restrictedCategory: z.boolean().default(false),
});
export type LandingCreate = z.infer<typeof landingCreateSchema>;

export const landingMetaUpdateSchema = z.object({
  name: z.string().trim().min(2).max(120).optional(),
  slug: z.string().trim().max(200).optional(),
  translationStatus: z.enum(translationStatuses).optional(),
  expectedRevision: z.number().int().min(0).optional(),
});
export type LandingMetaUpdate = z.infer<typeof landingMetaUpdateSchema>;

export const duplicateSchema = z.object({ name: z.string().trim().min(2).max(120), slug: z.string().trim().max(200).optional() });
export const translateSchema = z.object({ locale: z.string().trim().min(2).max(10), slug: z.string().trim().max(200).optional() });
export const scheduleSchema = z.object({ at: z.string().datetime(), expectedRevision: z.number().int().min(0) });
export const publishWithSummarySchema = z.object({ expectedRevision: z.number().int().min(0), changeSummary: z.string().trim().max(240).optional() });
export const restoreSchema = z.object({ expectedRevision: z.number().int().min(0) });

export const landingListItemSchema = z.object({
  id: z.string(),
  landingId: z.string(),
  key: z.string(),
  name: z.string(),
  kind: pageKindSchema,
  locale: z.string(),
  slug: z.string(),
  state: z.enum(landingStates),
  translationStatus: z.enum(translationStatuses),
  draftRevision: z.number().int(),
  hasUnpublishedChanges: z.boolean(),
  publishedVersionNumber: z.number().int().nullable(),
  scheduledFor: z.string().nullable(),
  restrictedCategory: z.boolean(),
  updatedAt: z.string(),
});
export type LandingListItem = z.infer<typeof landingListItemSchema>;

/** The simple, honest page lifecycle shown in lists. */
export function lifecycleOf(i: Pick<LandingListItem, 'state' | 'publishedVersionNumber' | 'hasUnpublishedChanges'>): 'archived' | 'scheduled' | 'review' | 'published' | 'published-changes' | 'draft' {
  if (i.state === 'ARCHIVED') return 'archived';
  if (i.state === 'SCHEDULED') return 'scheduled';
  if (i.state === 'REVIEW') return 'review';
  if (i.publishedVersionNumber !== null) return i.hasUnpublishedChanges ? 'published-changes' : 'published';
  return 'draft';
}

// ---------- Versions ----------
export const versionSummarySchema = z.object({
  versionNumber: z.number().int(),
  versionId: z.string(),
  publishedAt: z.string(),
  publishedByName: z.string().nullable(),
  changeSummary: z.string().nullable(),
  slug: z.string(),
  isLive: z.boolean(),
});
export type VersionSummary = z.infer<typeof versionSummarySchema>;

export const blockChangeSchema = z.object({
  id: z.string(),
  type: z.string(),
  change: z.enum(['added', 'removed', 'changed', 'moved', 'unchanged']),
  /** Dotted field paths whose values differ (for `changed`). */
  fields: z.array(z.string()),
});
export const compareResultSchema = z.object({
  from: z.object({ label: z.string(), content: landingContentSchema }),
  to: z.object({ label: z.string(), content: landingContentSchema }),
  blocks: z.array(blockChangeSchema),
  seoFields: z.array(z.string()),
  identical: z.boolean(),
});
export type CompareResult = z.infer<typeof compareResultSchema>;

// ---------- SEO checks (step 1 §26): actionable, never a fake score ----------
export const seoCheckSchema = z.object({
  key: z.string(),
  label: z.string(),
  status: z.enum(['pass', 'warn', 'fail']),
  detail: z.string(),
  /** A failing blocker prevents publishing; a warning does not. */
  blocker: z.boolean(),
});
export type SeoCheck = z.infer<typeof seoCheckSchema>;
export const seoReportSchema = z.object({ checks: z.array(seoCheckSchema), blockers: z.number().int(), warnings: z.number().int(), canonical: z.string(), jsonLdTypes: z.array(z.string()) });
export type SeoReport = z.infer<typeof seoReportSchema>;

// ---------- Redirects ----------
export const redirectCreateSchema = z.object({
  source: z.string().trim().min(1).max(300),
  destination: z.string().trim().max(300).optional(),
  statusCode: z.union([z.literal(301), z.literal(302), z.literal(410)]).default(301),
  reason: z.string().trim().max(240).optional(),
});
export type RedirectCreate = z.infer<typeof redirectCreateSchema>;
export const redirectViewSchema = z.object({ id: z.string(), source: z.string(), destination: z.string().nullable(), statusCode: z.number().int(), reason: z.string().nullable(), auto: z.boolean(), hits: z.number().int(), createdAt: z.string(), lastHitAt: z.string().nullable() });
export type RedirectView = z.infer<typeof redirectViewSchema>;
export const notFoundViewSchema = z.object({ path: z.string(), count: z.number().int(), lastSeenAt: z.string(), lastReferrer: z.string().nullable(), suggestion: z.string().nullable() });
export type NotFoundView = z.infer<typeof notFoundViewSchema>;

// ---------- Public delivery ----------
export const alternateSchema = z.object({ locale: z.string(), hreflang: z.string(), path: z.string() });
export type Alternate = z.infer<typeof alternateSchema>;
export const sitemapEntrySchema = z.object({
  path: z.string(),
  lastModified: z.string(),
  robots: z.string(),
  group: z.enum(['pages', 'services', 'industries', 'solutions', 'case-studies', 'articles']),
  alternates: z.array(alternateSchema),
});
export type SitemapEntry = z.infer<typeof sitemapEntrySchema>;
export const resolveResultSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('redirect'), destination: z.string(), statusCode: z.number() }),
  z.object({ kind: z.literal('gone') }),
  z.object({ kind: z.literal('none') }),
]);
export type ResolveResult = z.infer<typeof resolveResultSchema>;

export { landingContentSchema, seoSchema };
